import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useSite } from '../../context/SiteContext';
import { toast } from 'react-hot-toast';

const normalizeImportKey = (value) => String(value ?? '')
  .replace(/^\uFEFF/, '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '');

const parseDelimitedLine = (line, delimiter) => {
  const values = [];
  let value = '';
  let quoted = false;

  for (let index = 0; index < line.length; index += 1) {
    const character = line[index];
    if (character === '"') {
      if (quoted && line[index + 1] === '"') {
        value += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
    } else if (character === delimiter && !quoted) {
      values.push(value.trim());
      value = '';
    } else {
      value += character;
    }
  }

  if (quoted) throw new Error('Hay una comilla sin cerrar en el archivo.');
  values.push(value.trim());
  return values;
};

const normalizeImportRow = (rawRow, sourceRow) => {
  const normalized = {};
  if (rawRow && typeof rawRow === 'object' && !Array.isArray(rawRow)) {
    Object.entries(rawRow).forEach(([key, value]) => {
      normalized[normalizeImportKey(key)] = typeof value === 'string' ? value.trim() : value;
    });
  }

  const firstValue = (...keys) => {
    const key = keys.find((candidate) => normalized[candidate] !== undefined && normalized[candidate] !== null);
    return key ? normalized[key] : '';
  };

  const textValue = (value) => {
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    return value;
  };

  return {
    source_row: sourceRow,
    item: textValue(firstValue('item', 'sku', 'id', 'codigo')),
    name: textValue(firstValue('name', 'nombre', 'description', 'descripcion')),
    bag_size_group: textValue(firstValue('bag_size_group', 'bag_size', 'size_group', 'grupo_tamano')),
    bag_style: textValue(firstValue('bag_style', 'style', 'estilo')),
    packing_group: textValue(firstValue('packing_group', 'packing', 'grupo_empaque')),
    lbs_factor: firstValue('lbs_factor', 'factor', 'lbs'),
  };
};

const parseImportData = (input) => {
  const text = input.trim();
  if (!text) throw new Error('Pega al menos un registro para importar.');

  if (text.startsWith('[') || text.startsWith('{')) {
    let json;
    try {
      json = JSON.parse(text);
    } catch {
      throw new Error('El JSON no es valido. Revisa comas, llaves y comillas.');
    }
    if (!Array.isArray(json) || json.length === 0) {
      throw new Error('El JSON debe ser un arreglo con al menos un registro.');
    }
    return json.map((row, index) => normalizeImportRow(row, index + 1));
  }

  const sourceLines = input.replace(/\r/g, '').split('\n')
    .map((line, index) => ({ line, sourceRow: index + 1 }))
    .filter(({ line }) => line.trim() !== '');
  if (sourceLines.length < 2) {
    throw new Error('El CSV debe incluir encabezados y al menos una fila de datos.');
  }

  const delimiter = sourceLines[0].line.includes('\t') ? '\t' : ',';
  const headers = parseDelimitedLine(sourceLines[0].line, delimiter).map(normalizeImportKey);
  if (headers.every((header) => !header)) throw new Error('No se pudieron leer los encabezados.');

  return sourceLines.slice(1).map(({ line, sourceRow }) => {
    const values = parseDelimitedLine(line, delimiter);
    const row = {};
    headers.forEach((header, index) => { row[header] = values[index] ?? ''; });
    return normalizeImportRow(row, sourceRow);
  });
};

const EMPTY_FILTERS = {
  item: '',
  name: '',
  bag_size_group: '',
  bag_style: '',
  packing_group: '',
};

const SORTABLE_COLUMNS = [
  { key: 'item', label: 'Item' },
  { key: 'name', label: 'Name' },
  { key: 'bag_size_group', label: 'Bag Size Group' },
  { key: 'bag_style', label: 'Bag Style' },
  { key: 'packing_group', label: 'Packing Group' },
  { key: 'lbs_factor', label: 'Lbs Factor' },
];

const isMissingLbsFactor = (value) => value === null || value === '' || Number(value) <= 0;

const OrdersData = () => {
  const { isAuthenticated, can } = useAuth();
  const canManageOrdersData = can('conversion:ordersdata') || can('catalogs:manage');
  const { getCurrentSiteId, currentSite } = useSite();
  
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [sort, setSort] = useState({ by: 'item', direction: 'asc' });
  const [missingLbsOnly, setMissingLbsOnly] = useState(false);
  const [quickFactors, setQuickFactors] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importData, setImportData] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [form, setForm] = useState({
    item: '',
    name: '',
    bag_size_group: '',
    bag_style: '',
    packing_group: 'M',
    lbs_factor: ''
  });

  const load = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      
      const siteId = getCurrentSiteId();
      
      const params = {
        page,
        per_page: 50,
        sort_by: sort.by,
        sort_direction: sort.direction,
      };
      if (siteId) {
        params.site_id = siteId;
      }
      if (appliedSearch) {
        params.search = appliedSearch;
      }
      if (missingLbsOnly) {
        params.missing_lbs_factor = 1;
      }
      Object.entries(appliedFilters).forEach(([key, value]) => {
        if (value) params[key] = value;
      });
      
      const { data } = await axios.get('/orders-data', { params });
      setItems(data.data);
      setCurrentPage(data.current_page);
      setTotalPages(data.last_page);
    } catch (error) {
      console.error('❌ OrdersData - Error loading:', error);
      toast.error('Error al cargar los datos');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, appliedSearch, getCurrentSiteId, missingLbsOnly, sort]);

  useEffect(() => {
    if (isAuthenticated()) {
      load(1);
    }
  }, [currentSite, isAuthenticated, load]);

  const onChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const onCreate = async () => {
    try {
      const siteId = getCurrentSiteId();
      const payload = { ...form };
      
      if (siteId) {
        payload.site_id = siteId;
      }
      
      await axios.post('/orders-data', payload);
      setForm({ item: '', name: '', bag_size_group: '', bag_style: '', packing_group: 'M', lbs_factor: '' });
      toast.success('Registro creado exitosamente');
      load(currentPage);
    } catch (error) {
      console.error('Error creating:', error);
      toast.error(error.response?.data?.message || 'Error al crear el registro');
    }
  };

  const onUpdate = async () => {
    try {
      await axios.put(`/orders-data/${editing}`, form);
      setEditing(null);
      setForm({ item: '', name: '', bag_size_group: '', bag_style: '', packing_group: 'M', lbs_factor: '' });
      toast.success('Registro actualizado exitosamente');
      load(currentPage);
    } catch (error) {
      console.error('Error updating:', error);
      toast.error(error.response?.data?.message || 'Error al actualizar el registro');
    }
  };

  const saveQuickLbsFactor = async (item) => {
    const lbsFactor = Number(quickFactors[item.item]);
    if (!Number.isFinite(lbsFactor) || lbsFactor <= 0) {
      toast.error('Ingresa un factor LBS mayor que cero');
      return;
    }

    try {
      await axios.put(`/orders-data/${item.item}`, {
        item: item.item,
        name: item.name,
        bag_size_group: item.bag_size_group,
        bag_style: item.bag_style,
        packing_group: item.packing_group,
        lbs_factor: lbsFactor,
      });
      setQuickFactors((current) => {
        const next = { ...current };
        delete next[item.item];
        return next;
      });
      toast.success(`Factor LBS guardado para ${item.item}`);
      load(currentPage);
    } catch (error) {
      toast.error(error.response?.data?.message || 'No fue posible guardar el factor LBS');
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este registro?')) return;
    try {
      await axios.delete(`/orders-data/${id}`);
      toast.success('Registro eliminado exitosamente');
      load(currentPage);
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Error al eliminar el registro');
    }
  };

  const startEdit = (item) => {
    setEditing(item.item);
    setForm({
      item: item.item,
      name: item.name,
      bag_size_group: item.bag_size_group,
      bag_style: item.bag_style,
      packing_group: item.packing_group,
      lbs_factor: item.lbs_factor
    });
  };

  const cancelEdit = () => {
    setEditing(null);
    setForm({ item: '', name: '', bag_size_group: '', bag_style: '', packing_group: 'M', lbs_factor: '' });
  };

  const openImportModal = () => {
    setImportResult(null);
    setShowImportModal(true);
  };

  const closeImportModal = () => {
    if (importing) return;
    setShowImportModal(false);
    setImportData('');
    setImportResult(null);
  };

  const handleImport = async () => {
    setImportResult(null);
    setImporting(true);
    try {
      const parsedData = parseImportData(importData);

      const siteId = getCurrentSiteId();
      const payload = { data: parsedData };
      if (siteId) {
        payload.site_id = siteId;
      }

      const response = await axios.post('/orders-data/bulk-import', payload);
      const result = response.data;
      setImportResult(result);
      await load(1);

      if (result.failed > 0) {
        const summary = `${result.imported} registros guardados y ${result.failed} rechazados`;
        result.imported > 0 ? toast.success(summary) : toast.error(summary);
      } else {
        toast.success(`${result.imported} registros importados/actualizados exitosamente`);
        setShowImportModal(false);
        setImportData('');
        setImportResult(null);
      }
    } catch (error) {
      console.error('Error importing:', error);
      const message = error.response?.data?.message || error.message || 'Error al importar los datos';
      setImportResult({ parse_error: message });
      toast.error(message);
    } finally {
      setImporting(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setAppliedSearch(search.trim());
    setAppliedFilters({ ...filters });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((current) => ({ ...current, [name]: value }));
  };

  const clearFilters = () => {
    setSearch('');
    setAppliedSearch('');
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setMissingLbsOnly(false);
  };

  const changeSort = (column) => {
    setSort((current) => ({
      by: column,
      direction: current.by === column && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const sortIndicator = (column) => {
    if (sort.by !== column) return '↕';
    return sort.direction === 'asc' ? '↑' : '↓';
  };

  const hasActiveCriteria = Boolean(
    appliedSearch || missingLbsOnly || Object.values(appliedFilters).some(Boolean)
  );

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-3xl font-extrabold text-blue-900">Orders Data</h1>
        <div className="flex gap-3 items-center">
          {canManageOrdersData && (
            <button
              onClick={openImportModal}
              className="px-4 py-2 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition shadow"
            >
              📥 Importar Datos
            </button>
          )}
          {currentSite && (
            <div className="px-4 py-2 bg-blue-100 text-blue-800 rounded-lg font-semibold border-2 border-blue-300">
              📍 {currentSite.code} - {currentSite.name}
            </div>
          )}
        </div>
      </div>

      {/* Search and filters */}
      <form onSubmit={handleSearch} className="mb-6 rounded-2xl border border-blue-100 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 md:flex-row">
          <div className="flex-1">
            <label htmlFor="orders-search" className="mb-1 block text-sm font-semibold text-gray-800">
              Búsqueda general
            </label>
            <input
              id="orders-search"
              type="text"
              placeholder="Item, Name, Bag Size, Bag Style o Packing..."
              className="w-full rounded border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-400"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex items-end gap-2">
            <button
              type="submit"
              className="px-4 py-2 bg-blue-700 text-white font-semibold rounded-lg hover:bg-blue-800 transition shadow"
            >
              Aplicar filtros
            </button>
            {(hasActiveCriteria || search || Object.values(filters).some(Boolean)) && (
              <button
                type="button"
                onClick={clearFilters}
                className="px-4 py-2 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition"
              >
                Limpiar
              </button>
            )}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {SORTABLE_COLUMNS.slice(0, 5).map(({ key, label }) => (
            <div key={key}>
              <label htmlFor={`filter-${key}`} className="mb-1 block text-xs font-bold uppercase tracking-wide text-gray-600">
                {label}
              </label>
              <input
                id={`filter-${key}`}
                name={key}
                type="text"
                value={filters[key]}
                onChange={handleFilterChange}
                placeholder={`Filtrar ${label}`}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-blue-400"
              />
            </div>
          ))}
        </div>
        <label className={`mt-4 inline-flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 text-sm font-bold transition ${missingLbsOnly ? 'border-amber-400 bg-amber-50 text-amber-900' : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-amber-300'}`}>
          <input
            type="checkbox"
            checked={missingLbsOnly}
            onChange={(event) => setMissingLbsOnly(event.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
          />
          Mostrar únicamente Items sin factor LBS
        </label>
        <p className="mt-3 text-xs text-gray-500">
          Los filtros se combinan entre sí. Usa los encabezados de la tabla para ordenar.
        </p>
      </form>

      {/* Form */}
      {canManageOrdersData && <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-blue-100">
        <h2 className="text-xl font-bold text-blue-900 mb-4">{editing ? 'Editar Registro' : 'Nuevo Registro'}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">Item</label>
            <input
              name="item"
              type="text"
              className="w-full rounded border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-400"
              value={form.item}
              onChange={onChange}
              placeholder="18895"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-gray-800 mb-1">Name</label>
            <input
              name="name"
              type="text"
              className="w-full rounded border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-400"
              value={form.name}
              onChange={onChange}
              placeholder="CRAVE CUPCAKES - GRANDE"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">Bag Size Group</label>
            <input
              name="bag_size_group"
              type="text"
              className="w-full rounded border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-400"
              value={form.bag_size_group}
              onChange={onChange}
              placeholder="GRANDE"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">Bag Style</label>
            <input
              name="bag_style"
              type="text"
              className="w-full rounded border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-400"
              value={form.bag_style}
              onChange={onChange}
              placeholder="Custom Mod"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">Packing Group</label>
            <select
              name="packing_group"
              className="w-full rounded border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-400"
              value={form.packing_group}
              onChange={onChange}
            >
              <option value="">— Seleccionar —</option>
              <option value="S">S</option>
              <option value="M">M</option>
              <option value="L">L</option>
              <option value="XL">XL</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">Lbs Factor</label>
            <input
              name="lbs_factor"
              type="number"
              step="0.000000001"
              className="w-full rounded border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-400"
              value={form.lbs_factor}
              onChange={onChange}
              placeholder="0.223165448"
            />
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          {editing ? (
            <>
              <button
                className="px-4 py-2 bg-blue-700 text-white font-bold rounded-lg shadow hover:bg-blue-800 transition"
                onClick={onUpdate}
              >
                💾 Guardar
              </button>
              <button
                className="px-4 py-2 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition"
                onClick={cancelEdit}
              >
                Cancelar
              </button>
            </>
          ) : (
            <button
              className="px-4 py-2 bg-green-600 text-white font-bold rounded-lg shadow hover:bg-green-700 transition"
              onClick={onCreate}
            >
              ➕ Agregar
            </button>
          )}
        </div>
      </div>}

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-blue-100">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm text-left border border-gray-200 rounded-xl">
            <thead className="bg-blue-100 text-blue-900">
              <tr>
                {SORTABLE_COLUMNS.map(({ key, label }) => (
                  <th
                    key={key}
                    className="px-4 py-2 font-bold"
                    aria-sort={sort.by === key ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    <button
                      type="button"
                      onClick={() => changeSort(key)}
                      className="inline-flex items-center gap-1 whitespace-nowrap hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      title={`Ordenar por ${label}`}
                    >
                      {label}
                      <span aria-hidden="true" className={sort.by === key ? 'text-blue-700' : 'text-blue-400'}>
                        {sortIndicator(key)}
                      </span>
                    </button>
                  </th>
                ))}
                {canManageOrdersData && <th className="px-4 py-2 font-bold">Acciones</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={canManageOrdersData ? 7 : 6} className="px-4 py-8 text-center text-gray-500">
                    <div className="flex items-center justify-center">
                      <svg className="animate-spin h-5 w-5 mr-3 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Cargando...
                    </div>
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={canManageOrdersData ? 7 : 6} className="px-4 py-8 text-center text-gray-500">
                    No hay datos disponibles
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.item} className="even:bg-gray-50 hover:bg-blue-50">
                    <td className="px-4 py-2 font-semibold">{item.item}</td>
                    <td className="px-4 py-2">{item.name}</td>
                    <td className="px-4 py-2">{item.bag_size_group}</td>
                    <td className="px-4 py-2">{item.bag_style}</td>
                    <td className="px-4 py-2">{item.packing_group}</td>
                    <td className="px-4 py-2 font-mono">
                      {canManageOrdersData && isMissingLbsFactor(item.lbs_factor) ? (
                        <div className="flex min-w-[230px] items-center gap-2">
                          <input
                            type="number"
                            step="0.000000001"
                            min="0.000000001"
                            aria-label={`Factor LBS para ${item.item}`}
                            value={quickFactors[item.item] ?? ''}
                            onChange={(event) => setQuickFactors((current) => ({
                              ...current,
                              [item.item]: event.target.value,
                            }))}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') saveQuickLbsFactor(item);
                            }}
                            placeholder="Agregar factor"
                            className="w-36 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-slate-900 focus:ring-2 focus:ring-amber-500"
                          />
                          <button
                            type="button"
                            onClick={() => saveQuickLbsFactor(item)}
                            className="rounded-lg bg-amber-600 px-3 py-1 font-sans font-bold text-white hover:bg-amber-700"
                          >
                            Guardar
                          </button>
                        </div>
                      ) : (item.lbs_factor || '—')}
                    </td>
                    {canManageOrdersData && <td className="px-4 py-2 flex gap-2">
                      <button
                        className="px-3 py-1 bg-blue-50 text-blue-700 border border-blue-300 rounded-lg font-semibold hover:bg-blue-100 transition"
                        onClick={() => startEdit(item)}
                      >
                        ✏️ Editar
                      </button>
                      <button
                        className="px-3 py-1 bg-red-50 text-red-700 border border-red-300 rounded-lg font-semibold hover:bg-red-100 transition"
                        onClick={() => onDelete(item.item)}
                      >
                        🗑️ Eliminar
                      </button>
                    </td>}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-6">
            <button
              className="px-4 py-2 bg-blue-50 text-blue-700 border border-blue-300 rounded-lg font-semibold hover:bg-blue-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => load(currentPage - 1)}
              disabled={currentPage === 1}
            >
              ← Anterior
            </button>
            <span className="px-4 py-2 text-gray-700 font-semibold">
              Página {currentPage} de {totalPages}
            </span>
            <button
              className="px-4 py-2 bg-blue-50 text-blue-700 border border-blue-300 rounded-lg font-semibold hover:bg-blue-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => load(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Siguiente →
            </button>
          </div>
        )}
      </div>

      {/* Import Modal */}
      {canManageOrdersData && showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-blue-900">Importar Datos</h2>
              <button
                onClick={closeImportModal}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
              >
                ×
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-700 mb-2">
                <strong>Formato CSV:</strong> Pega los datos separados por comas o tabuladores. La primera línea debe contener los encabezados.
              </p>
              <p className="text-sm text-gray-700 mb-2">
                <strong>Formato JSON:</strong> Array de objetos con las propiedades: item, name, bag_size_group, bag_style, packing_group, lbs_factor
              </p>
              <div className="bg-gray-100 p-3 rounded-lg text-xs font-mono overflow-x-auto">
                <div className="mb-2"><strong>Ejemplo CSV:</strong></div>
                <div>item,name,bag_size_group,bag_style,packing_group,lbs_factor</div>
                <div>18895,CRAVE CUPCAKES - GRANDE,GRANDE,Custom Mod,XL,0.223165448</div>
                <div>18896,CRAVE CUPCAKES - CM REGAL,REGAL,Custom Mod,XL,0.138306608</div>
              </div>
            </div>

            <textarea
              className="w-full h-64 rounded border border-gray-300 px-3 py-2 text-sm font-mono text-gray-900 focus:ring-2 focus:ring-blue-400"
              placeholder="Pega aquí tus datos en formato CSV o JSON..."
              value={importData}
              onChange={(e) => setImportData(e.target.value)}
            />

            {importResult?.parse_error && (
              <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-800">
                {importResult.parse_error}
              </div>
            )}

            {importResult && !importResult.parse_error && (
              <div className="mt-4 space-y-3">
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-center">
                    <div className="text-xl font-extrabold text-slate-900">{importResult.received}</div>
                    <div className="text-xs font-semibold text-slate-500">Recibidos</div>
                  </div>
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-center">
                    <div className="text-xl font-extrabold text-emerald-800">{importResult.created}</div>
                    <div className="text-xs font-semibold text-emerald-700">Creados</div>
                  </div>
                  <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 text-center">
                    <div className="text-xl font-extrabold text-blue-800">{importResult.updated}</div>
                    <div className="text-xs font-semibold text-blue-700">Actualizados</div>
                  </div>
                  <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-center">
                    <div className="text-xl font-extrabold text-red-800">{importResult.failed}</div>
                    <div className="text-xs font-semibold text-red-700">Rechazados</div>
                  </div>
                </div>

                {importResult.errors?.length > 0 && (
                  <div className="overflow-hidden rounded-xl border border-red-200">
                    <div className="bg-red-50 px-4 py-2 text-sm font-bold text-red-800">
                      Registros que requieren corrección
                    </div>
                    <div className="max-h-56 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-white text-left text-xs uppercase text-slate-500">
                          <tr>
                            <th className="px-4 py-2">Fila</th>
                            <th className="px-4 py-2">Item</th>
                            <th className="px-4 py-2">Motivo</th>
                          </tr>
                        </thead>
                        <tbody>
                          {importResult.errors.map((rowError, index) => (
                            <tr key={`${rowError.row}-${rowError.item || index}`} className="border-t border-red-100">
                              <td className="px-4 py-2 font-bold text-slate-800">{rowError.row}</td>
                              <td className="px-4 py-2 font-mono text-slate-700">{rowError.item || '—'}</td>
                              <td className="px-4 py-2 text-red-800">{rowError.errors.join(' ')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="bg-red-50 px-4 py-2 text-xs text-red-700">
                      Los registros válidos ya fueron guardados. Corrige las filas indicadas y vuelve a importar si es necesario.
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={closeImportModal}
                className="px-4 py-2 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition"
                disabled={importing}
              >
                Cerrar
              </button>
              <button
                onClick={handleImport}
                className="px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition"
                disabled={!importData.trim() || importing}
              >
                {importing ? 'Validando…' : '🚀 Importar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersData;
